import type { ComponentType } from 'react';
import React from 'react';

import { Navigate, rootRouteId, useSearch } from '@tanstack/react-router';

import { useAuth } from '@dcentralexpedientedigital/fe-shared/hooks/use-auth';
import { useIntlStore } from '@dcentralexpedientedigital/fe-shared/stores/intl-store';
import { usePageRoute } from '@dcentralexpedientedigital/fe-ui/hooks/use-page-route';

interface IWithAuthOptions {
    OnRedirecting?: ComponentType;
}

export const withAuthenticationGuard = <P extends object>(
    Component: ComponentType<P>,
    options: IWithAuthOptions = {},
): React.FC<P> => {
    return (props: P) => {
        const { auth } = useAuth();
        const locale = useIntlStore((state) => state.locale);
        const currentSearchParams = useSearch({ from: rootRouteId });

        const loginRoute = usePageRoute('login');
        const { OnRedirecting } = options;

        if (auth.isAuthenticated) {
            return <Component {...props} />;
        }

        if (!auth.isLoading && !auth.isAuthenticated) {
            const originalProtectedPathname = location.pathname;

            const { backTo: _ignoredBackToParamForValue, ...paramsWithoutBackTo } =
                currentSearchParams;

            const queryStringForBackToValue = Object.keys(paramsWithoutBackTo)
                .map((key) => {
                    const value = paramsWithoutBackTo[key];
                    if (value !== undefined && value !== null) {
                        return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
                    }
                    return '';
                })
                .filter(Boolean)
                .join('&');

            const finalBackToValue = `${originalProtectedPathname}${queryStringForBackToValue ? `?${queryStringForBackToValue}` : ''}`;

            const finalSearchForLoginNavigation = {
                ...paramsWithoutBackTo,
                backTo: finalBackToValue,
            };

            return (
                <Navigate
                    to={`/${locale}${loginRoute}`}
                    search={finalSearchForLoginNavigation}
                    replace={true}
                />
            );
        }

        return OnRedirecting ? <OnRedirecting /> : null;
    };
};
